import { useState } from 'react';
import { Button } from '@/components/ui/Button';

export interface RequestClosureModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (note?: string) => Promise<void>;
}

export function RequestClosureModal({ isOpen, onClose, onSubmit }: RequestClosureModalProps) {
  const [note, setNote] = useState('');
  const [submitting, setSubmitting] = useState(false);

  if (!isOpen) { return null; }

  const handleSubmit = async () => {
    setSubmitting(true);
    try {
      await onSubmit(note.trim() ? note.trim() : undefined);
      setNote('');
      onClose();
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center bg-black/40 p-4 pt-16">
      <div className="bg-white dark:bg-secondary-900 rounded-lg shadow-xl p-6 w-full max-w-md">
        <h2 className="text-lg font-semibold text-secondary-900 dark:text-white mb-2">
          Request Closure
        </h2>
        <p className="text-sm text-secondary-600 dark:text-secondary-400 mb-4">
          Your supervisor will review and approve or reject this request. You can withdraw it at any
          time before it&apos;s approved.
        </p>
        <label
          htmlFor="closure-request-note"
          className="block text-sm font-medium text-secondary-700 dark:text-secondary-300 mb-1"
        >
          Note (optional)
        </label>
        <textarea
          id="closure-request-note"
          value={note}
          onChange={(e) => setNote(e.target.value)}
          rows={4}
          maxLength={2000}
          placeholder="Optional context for the supervisor..."
          className="w-full px-3 py-2 border border-secondary-300 dark:border-secondary-600 rounded-lg bg-white dark:bg-secondary-800 text-secondary-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
        />
        <div className="mt-4 flex justify-end space-x-2">
          <Button variant="outline" onClick={onClose} disabled={submitting}>
            Cancel
          </Button>
          <Button onClick={handleSubmit} loading={submitting}>
            Request Closure
          </Button>
        </div>
      </div>
    </div>
  );
}
